import React, { useCallback } from 'react'

import {
  Box,
  Button,
  ButtonColorVariant,
  Display,
  FlexDirection,
  Spinner,
} from '@gousto-internal/citrus-react'
import type Immutable from 'immutable'

import type { OrderConfirmationRafSuccessMessage } from '@library/api-customer-engagement-experience'

import { actionTypes } from 'actions/actionTypes'
import { CopyConfirmation } from 'components/SocialLinks/CopyConfirmation'
import { appendNativeShareUtm, type ShareSurface } from 'components/SocialLinks/shareNatively'
import {
  getMessage,
  getReferralLink,
  SHARE_TITLE,
} from 'components/SocialLinks/socialReferralHelper'
import { useShareWithFallback } from 'components/SocialLinks/useShareWithFallback'

type MobileShareLinkProps = {
  referralCode: string
  rafOffer: Immutable.Map<string, unknown>
  surface: ShareSurface
  label: string
  successMessage: Pick<OrderConfirmationRafSuccessMessage, 'title' | 'subtitle'>
  isReady: boolean
  onShare: () => void
  trackingReferFriend: (actionType: string, trackingType?: string) => void
  trackUserFreeFoodLinkShare: (payload: { target: string }) => void
}

export const MobileShareLink = ({
  referralCode,
  rafOffer,
  surface,
  label,
  successMessage,
  isReady,
  onShare,
  trackingReferFriend,
  trackUserFreeFoodLinkShare,
}: MobileShareLinkProps) => {
  const referralLink = getReferralLink(referralCode, '', '&utm_source=weblink')

  const onOpened = useCallback(
    () =>
      trackingReferFriend(actionTypes.REFER_FRIEND_LINK_SHARE, 'NativeReferFriendInterface Opened'),
    [trackingReferFriend],
  )

  const { isCopied, share } = useShareWithFallback({
    copyText: referralLink,
    surface,
    trackingReferFriend,
    trackUserFreeFoodLinkShare,
    onOpened,
  })

  const handleClick = () => {
    onShare()
    share({
      title: SHARE_TITLE,
      url: appendNativeShareUtm(getReferralLink(referralCode)),
      text: getMessage(rafOffer),
    })
  }

  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
      <Button
        onClick={handleClick}
        colorVariant={ButtonColorVariant.Secondary}
        width="100%"
        type="button"
        disabled={!isReady}
      >
        {isReady ? label : <Spinner size={6} />}
      </Button>
      {isCopied && <CopyConfirmation credit="" successMessage={successMessage} />}
    </Box>
  )
}
